<script setup lang="ts">
import { onMounted, watch, computed } from 'vue';

import { useIntegrationStore } from '@/modules/integrations/store/useIntegrationStore';
import { useOrganizationStore } from '@/store/useOrganizationStore';
import { storeToRefs } from 'pinia';
import { paragon } from '@useparagon/connect';
import IntegrationCard from './IntegrationCard.vue';

// Workflow integrations that require business/enterprise plans
const WORKFLOW_INTEGRATIONS = ['Zendesk', 'Jira', 'Confluence', 'Notion'];
const BUSINESS_PLAN_CODES = ['business-annual', 'business-monthly'];

const integrationStore = useIntegrationStore();
const { integration_token, integration_project_id } = storeToRefs(integrationStore);

const organizationStore = useOrganizationStore();
const { planCode } = storeToRefs(organizationStore);

// Check if current plan allows workflow integrations
const hasBusinessPlan = computed(() => {
  const currentPlan = planCode?.value?.toLowerCase() || '';
  return BUSINESS_PLAN_CODES.includes(currentPlan as (typeof BUSINESS_PLAN_CODES)[number]);
});

// Apply plan-based filtering to integrations
const enabledIntegrations = computed(() => {
  return integrationStore.integrations.map(integration => {
    // Check if this is a workflow integration that requires business plan (case-insensitive)
    const isWorkflowIntegration = WORKFLOW_INTEGRATIONS.some(workflowName => workflowName.toLowerCase() === integration.name.toLowerCase());

    if (isWorkflowIntegration && !hasBusinessPlan.value) {
      // Disable workflow integrations for non-business plans
      return { ...integration, enabled: false };
    }

    return integration;
  });
});

onMounted(() => {
  integrationStore.fetchIntegrations();
  // Ensure organization store is loaded if not already by a parent/route guard
  if (!organizationStore.id) {
    // This might be an issue if the store isn't loaded before this component.
    // Consider ensuring store initialization at a higher level (e.g., App.vue or route guard).
    console.warn('OrganizationStore might not be initialized in IntegrationsGrid');
  }
});

watch(integration_token, newToken => {
  paragon.authenticate(integration_project_id.value ?? '', newToken ?? '');
});

const toggleIntegration = async (name: string) => {
  await paragon.authenticate(integration_project_id.value ?? '', integration_token.value ?? '');
  paragon.connect(name.toLowerCase(), { onInstall: handleSuccess, onUninstall: handleSuccess });
};

const handleSuccess = () => {
  integrationStore.fetchIntegrations(true);
};
</script>

<template>
  <div class="integrations-container">
    <div v-if="integrationStore.loading">Loading...</div>
    <ul class="cards-grid">
      <IntegrationCard v-for="integration in enabledIntegrations" :key="integration.name" :integration="integration" @toggle="toggleIntegration" />
    </ul>
  </div>
</template>
<style scoped>
.integrations-container {
  @apply py-8;
}
.cards-grid {
  @apply grid gap-6 mt-4;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
}
</style>
